/* THE REGISTER IS A COLUMN OF CHECKBOXES, and this screen is the only place in the
 * product that can write a check-in. The member record deliberately cannot.
 *
 * A coach marking a room is standing up with a phone in one hand, so the cost that
 * matters is CLICKS PER PERSON, not screens per session. The old flow was type a
 * name, read the hits, press a button: three actions and a decision per member. The
 * roster is already on screen in the order a register is read, and marking somebody
 * is one click anywhere on their row. Ticking is reversible, which is the whole
 * reason a checkbox is the honest control here.
 *
 * THE ROSTER DOES NOT REORDER AS YOU TICK. A list that resorts under your thumb costs
 * you your place. The source sorts once, alphabetically, and the order survives every
 * write for the life of this dialog.
 *
 * A SUPPRESSED MEMBER IS A PERSON IN THE ROOM LIKE ANYBODY ELSE. They keep the facts
 * and lose the colour, they are never in the counts, and they are offered a note but
 * never a follow-up: a follow-up is a chase (SITE-RULES 1).
 */
import { useState } from 'react';
import { useAsync, useSource } from '../../data/SourceProvider.js';
import type { RegisterRow } from '../../data/contract.js';
import { Modal } from '../../components/Modal.js';
import { Avatar } from '../../components/Avatar.js';
import { NoneRow } from '../../components/Panel.js';
import { useToast } from '../../components/Toast.js';
import { fmt } from '../../lib/fmt.js';
import { go } from '../../routes.js';

export function Register({ classId, date, onClose, onWrote }: {
  classId: string; date: string; onClose: () => void; onWrote: () => void;
}) {
  const source = useSource();
  const [epoch, setEpoch] = useState(0);
  const { data, error } = useAsync(() => source.getRegister(classId, date), [source, classId, date, epoch]);

  const redraw = () => { setEpoch((n) => n + 1); onWrote(); };

  return (
    <Modal
      title={data ? `${data.cls.name} · ${data.cls.time}` : 'Register'}
      onClose={onClose}
      footer={<button type="button" className="btn t-label-medium" onClick={onClose}>Done</button>}
    >
      {error && <NoneRow>Could not read the register. {error.message}</NoneRow>}
      {!error && !data && <NoneRow>Reading the room…</NoneRow>}
      {data && (
        <>
          <div className="eyebrow t-label-xsmall">
            {data.cls.instructor ?? 'Self-guided'} · {date === data.date && data.writable ? 'today' : fmt.date(date)}
            {' · '}{data.inCount} of {data.cls.cap ?? '—'} in
          </div>
          {!data.rows.length ? (
            <NoneRow>{data.writable ? 'Nobody on the roster yet.' : 'Nobody recorded in this session.'}</NoneRow>
          ) : (
            <div className="rows register">
              {data.rows.map((r) => (
                <Row key={r.member.id} r={r} classId={classId} date={date}
                  writable={data.writable} onWrote={redraw} onLeave={onClose} />
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function Row({ r, classId, date, writable, onWrote, onLeave }: {
  r: RegisterRow; classId: string; date: string; writable: boolean;
  onWrote: () => void; onLeave: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [noting, setNoting] = useState(false);
  const [text, setText] = useState('');
  const m = r.member;
  const sup = m.suppression;

  async function toggle() {
    setBusy(true);
    try {
      if (r.in) await source.undoCheckIn(m.id, classId, date);
      else await source.checkIn(m.id, classId, date);
      onWrote();
    } catch {
      toast(r.in ? 'Could not undo' : 'Check-in failed', 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function save(kind: 'note' | 'followup') {
    const t = text.trim();
    if (!t) { toast('Write a line first', 'bad'); return; }
    setBusy(true);
    try {
      await source.addRegisterNote(m.id, classId, t, kind);
      setText(''); setNoting(false);
      toast(`${kind === 'note' ? 'Note' : 'Follow-up'} saved to ${m.firstName}’s timeline`, 'good');
      onWrote();
    } catch (e) {
      toast((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cxentry">
      {/* The ticked state colours the WHOLE row, the Note button included. The label
          cannot carry it: it stops before those two controls, and a green band that
          ends two thirds of the way across reads as a rendering fault. */}
      <div className={'cxrow' + (r.in ? ' on' : '')}>
        {/* The label wraps the box AND the name, so the click target is the whole row.
            The arrow and the Note button sit outside it; inside, opening a record
            would silently tick the box on the way. */}
        <label className="row cx">
          <input
            type="checkbox" className="cbx" checked={r.in}
            disabled={!writable || busy} onChange={() => void toggle()}
          />
          <Avatar name={m.name} photoUrl={m.photoUrl} size="md" />
          <div className="who">
            <div className="n t-label-large">
              {m.name}
              {/* "No anchor" is a PROMPT to introduce them to somebody. On a
                  suppressed member it is a fact, not a prompt, so it keeps the word
                  and drops the amber. */}
              {!m.anchors.length && (
                <span className={'pill ' + (sup ? 'flat' : 'warn') + ' t-caption'}>No anchor</span>
              )}
              {sup && (
                <span className="pill flat t-caption">{sup.kind === 'care' ? 'Care Track' : 'On hold'}</span>
              )}
            </div>
            {/* An empty line under every name on a seat that is not sent plans is
                furniture. It appears when there is something to say. */}
            {m.plan && <div className="s t-caption muted">{m.plan}</div>}
            {r.context.length > 0 && (
              <div className="ctx">
                {r.context.map((c) => (
                  <span className={'pill ' + c.tone + ' t-caption'} key={c.key} title={c.why}>{c.text}</span>
                ))}
              </div>
            )}
          </div>
        </label>
        <button
          type="button" className={'ctx-btn t-label-small' + (noting ? ' on' : '')}
          onClick={() => setNoting((v) => !v)}
          title={`Add a note to ${m.name}’s timeline`}
        >Note</button>
        <button
          type="button" className="go t-label-medium" title="Open record"
          onClick={() => { onLeave(); go('/members/' + m.id); }}
        >→</button>
      </div>

      {noting && (
        <div className="ctx-note">
          <textarea
            className="field" rows={2} autoFocus value={text}
            placeholder={`What you noticed, or what you said to ${m.firstName}.`}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="ctx-acts">
            <button type="button" className="btn primary t-label-small" disabled={busy}
              onClick={() => void save('note')}>Save note</button>
            {/* A follow-up is a chase, and a suppressed member is never chased. */}
            {!sup && (
              <button type="button" className="btn t-label-small" disabled={busy}
                onClick={() => void save('followup')}>Flag follow-up</button>
            )}
            <button type="button" className="linkish t-label-small"
              onClick={() => { setNoting(false); setText(''); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
